"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, TrendingUp, Sparkles, CircleCheck as CheckCircle2, Users, Target, ShieldCheck, Zap, ListFilter as Filter } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
const statChips = [{
  id: "chip-1",
  label: "More Qualified Leads",
  icon: Users
}, {
  id: "chip-2",
  label: "Higher Close Rate",
  icon: Target
}, {
  id: "chip-3",
  label: "Zero Missed Inquiries",
  icon: ShieldCheck
}];
const funnelStages = [{
  stage: "01. AI & Local Traffic",
  label: "Raw Search Queries",
  count: "2,480",
  pct: "100%",
  width: "w-full",
  badge: "Top Funnel"
}, {
  stage: "02. AI Visibility Checkup",
  label: "High-Intent Prospects",
  count: "1,240",
  pct: "50%",
  width: "w-[82%]",
  badge: "Filtered Intent"
}, {
  stage: "03. Optimized Landing Experience",
  label: "Engaged Enquiries",
  count: "620",
  pct: "25%",
  width: "w-[64%]",
  badge: "Nurtured"
}, {
  stage: "04. Won Customers & Bookings",
  label: "Paying Clients",
  count: "318",
  pct: "12.8%",
  width: "w-[46%]",
  badge: "Converted"
}];
export default function Funnel() {
  const shouldReduceMotion = useReducedMotion();
  const [rate, setRate] = useState(3.2);
  useEffect(() => {
    if (shouldReduceMotion) return;
    const timer = setInterval(() => {
      setRate(prev => prev >= 12.8 ? 3.2 : parseFloat((prev + 0.3).toFixed(1)));
    }, 200);
    return () => clearInterval(timer);
  }, [shouldReduceMotion]);
  return <section id="heroconversionfunnel" data-nav="dark" className="relative bg-background text-foreground py-32 md:py-44 overflow-hidden border-b border-border/40">
      {/* Background glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[var(--accent-deep-red)]/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column - Main Copy */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-8 space-y-8 text-left">
            {/* Tagline Badge */}
            <div className="inline-flex items-center gap-2">
              <Badge variant="outline" className="bg-accent/50 border-border text-muted-foreground uppercase tracking-wider text-xs px-3.5 py-1.5 rounded-full">
                <Sparkles className="size-3.5 text-[var(--accent-deep-red)]" />
                <span>LEAD GENERATION & CONVERSION OPTIMIZATION</span>
              </Badge>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground leading-[1.08] font-default">
              When someone asks AI who to hire, you should get{" "}
              <span className="text-[var(--accent-deep-red)] italic font-serif underline decoration-[var(--accent-deep-red)]/30 decoration-wavy underline-offset-8">
                Converted
              </span>
            </h1>

            {/* Subtitle */}
            <p className="max-w-2xl text-lg sm:text-xl text-muted-foreground leading-relaxed font-normal">
              Generating leads is only half the battle. The real goal is turning high-intent visitors into paying customers. ClearLine GEO helps attract higher-quality leads and optimize every step of the customer journey to maximize conversions.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button size="lg" className="bg-[var(--accent-deep-red)] text-white hover:bg-[var(--accent-deep-red)]/90 shadow-md px-7 py-6 text-base font-semibold rounded-xl group relative overflow-hidden transition-all duration-300" asChild>
                <Link className="underline cursor-pointer" to="/PricingPlansPage">
                  <span className="size-2.5 rounded-full bg-white animate-pulse" />
                  <span>Get started </span>
                  <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button size="lg" variant="outline" className="border-border text-foreground hover:border-foreground/40 hover:bg-accent/60 px-7 py-6 text-base font-medium rounded-xl transition-all duration-300" asChild>
                <Link to="/VisibilityCheckupPage" className="inline-flex items-center justify-center gap-2">
                  <span>Free AI Visibility Checkup</span>
                </Link>
              </Button>
            </div>

            <Separator className="bg-border/60 my-6" />

            {/* Trust Stat Chips */}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              {statChips.map((chip, i) => {
              const IconComponent = chip.icon;
              return <div key={chip.id} data-index={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border/80 bg-card/60 backdrop-blur-sm text-sm font-medium text-foreground/90 shadow-2xs hover:border-[var(--accent-deep-red)]/40 transition-colors">
                    <IconComponent className="size-4 text-[var(--accent-deep-red)]" />
                    <span>{chip.label}</span>
                  </div>;
            })}
            </div>
          </motion.div>

          {/* Right Column - Animated Funnel Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-4 relative">
            <div className="relative bg-card/80 border border-border/80 rounded-2xl p-6 shadow-2xl backdrop-blur-md overflow-hidden">
              {/* Card Header */}
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-border/60">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-[var(--accent-deep-red)]/10 rounded-lg text-[var(--accent-deep-red)]">
                    <Filter className="size-5" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-foreground">Conversion Engine</h2>
                    <p className="text-xs text-muted-foreground">Live Funnel Flow</p>
                  </div>
                </div>
                
                {/* Live Rate Metric */}
                <div className="text-right">
                  <div className="text-xs text-muted-foreground font-medium">Conversion Rate</div>
                  <div className="text-lg font-bold text-[var(--accent-deep-red)] font-mono flex items-center justify-end gap-1">
                    <TrendingUp className="size-4" />
                    <span>{rate.toFixed(1)}%</span>
                  </div>
                </div>
              </div>

              {/* Traffic Nodes Simulation Bar */}
              <div className="relative h-12 bg-accent/40 rounded-lg mb-6 overflow-hidden flex items-center justify-around px-4 border border-border/40">
                {[...Array(8)].map((_, idx) => <motion.div key={idx} data-index={idx} animate={shouldReduceMotion ? {} : {
                y: [0, 16, 0],
                opacity: [0.3, 1, 0.3]
              }} transition={{
                duration: 2 + idx % 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: idx * 0.2
              }} className="size-2 rounded-full bg-[var(--accent-deep-red)]/80" />)}
                <span className="text-[11px] font-mono text-muted-foreground uppercase tracking-widest z-10 bg-card/80 px-2 py-0.5 rounded border border-border/50">
                  Scattered AI Traffic
                </span>
              </div>

              {/* Funnel Visual Stack */}
              <div className="space-y-3 relative">
                {funnelStages.map((stage, i) => <div key={stage.stage} data-index={i} className="flex flex-col items-center">
                    <div className={`${stage.width} bg-accent/60 hover:bg-accent/90 border border-border/60 rounded-xl p-3 transition-all duration-300 relative group overflow-hidden shadow-2xs`}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-semibold text-foreground/90">{stage.stage}</span>
                        <Badge variant="secondary" className="text-[10px] px-2 py-0 h-4 bg-background/80 text-muted-foreground">
                          {stage.badge}
                        </Badge>
                      </div>
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs text-muted-foreground">{stage.label}</span>
                        <div className="flex items-baseline gap-1.5 font-mono">
                          <span className="text-sm font-bold text-foreground">{stage.count}</span>
                          <span className="text-[10px] text-[var(--accent-deep-red)] font-semibold">({stage.pct})</span>
                        </div>
                      </div>
                      
                      {/* Highlight Bottom Stage */}
                      {i === funnelStages.length - 1 && <div className="absolute inset-0 border border-[var(--accent-deep-red)]/60 rounded-xl pointer-events-none bg-[var(--accent-deep-red)]/5" />}
                    </div>
                  </div>)}
              </div>

              {/* Status Footer */}
              <div className="mt-6 pt-4 border-t border-border/60 flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <CheckCircle2 className="size-4 text-[var(--accent-deep-red)]" />
                  <span>Optimized Touchpoints Active</span>
                </div>
                <span className="font-mono text-foreground font-semibold">Ready to Scale</span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}